import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { SITE } from "../lib/site";

const links = [
    { label: "Gallery", href: "#gallery" },
    { label: "Reviews", href: "#reviews" },
    { label: "Contact", href: "#book" },
];

export const Nav = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <motion.header
            data-testid="site-nav"
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
                scrolled || open
                    ? "bg-[#0a0a0e]/80 backdrop-blur-xl border-b border-white/10"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
                <a href="#top" data-testid="nav-logo" className="flex items-center gap-3" onClick={() => setOpen(false)}>
                    <img src="/assets/logo-mark.png" alt="Pronto Auto Care" className="h-8 w-auto" />
                    <span className="hidden font-display font-bold tracking-tight text-sm sm:text-base uppercase">
                        Pronto <span className="text-zinc-400">Auto Care</span>
                    </span>
                </a>

                <nav className="hidden md:flex items-center gap-10">
                    {links.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className="font-code text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-white transition-colors duration-300"
                        >
                            {l.label}
                        </a>
                    ))}
                    <motion.a
                        data-testid="nav-booksy-button"
                        href={SITE.booksy}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                        className="inline-flex items-center gap-2 bg-white text-black text-sm font-semibold px-5 py-2.5 rounded-full"
                    >
                        Book Now <ArrowUpRight className="w-4 h-4" />
                    </motion.a>
                </nav>

                <button
                    data-testid="nav-mobile-menu-button"
                    onClick={() => setOpen(!open)}
                    className="md:hidden p-2 text-white"
                    aria-label="Toggle menu"
                >
                    {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        data-testid="nav-mobile-menu"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="md:hidden overflow-hidden border-t border-white/10"
                    >
                        <div className="px-6 py-6 flex flex-col gap-5">
                            {links.map((l) => (
                                <a
                                    key={l.href}
                                    href={l.href}
                                    onClick={() => setOpen(false)}
                                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                                    className="font-display text-2xl font-bold tracking-tight"
                                >
                                    {l.label}
                                </a>
                            ))}
                            <a
                                data-testid="nav-mobile-booksy-button"
                                href={SITE.booksy}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center gap-2 bg-white text-black font-semibold px-5 py-3.5 rounded-full mt-2"
                            >
                                Book Now <ArrowUpRight className="w-4 h-4" />
                            </a>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </motion.header>
    );
};
